import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { useAuth } from "@/contexts/AuthContext";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import type { DBFollowUp, FollowUpChannel, FollowUpPriority } from "@/lib/db-types";

/**
 * Oppfølginger (CRM-2.2), opprettelse og redigering. Lukking og utsettelse
 * ligger i use-close-follow-up.ts. Audit skrives av DB-triggeren med innlogget
 * bruker som aktør.
 */

export const onError = (prefix: string) => (err: Error) => toast.error(`${prefix}: ${err.message}`);

export function useInvalidateFollowUps() {
  const qc = useQueryClient();
  return async (row: Pick<DBFollowUp, "owner_id" | "customer_id">) => {
    await invalidateFor(qc, "follow_ups", { ownerId: row.owner_id, customerId: row.customer_id });
  };
}

export interface CreateFollowUpInput {
  customer_id: string;
  title: string;
  due_date: string;
  priority: FollowUpPriority;
  channel?: FollowUpChannel | null;
  opportunity_id?: string | null;
  contact_id?: string | null;
  description?: string | null;
  /** Utelatt = jeg selv. null = uten eier. */
  owner_id?: string | null;
}

export function useCreateFollowUp() {
  const { user } = useAuth();
  const { activeUser } = useImpersonation();
  const invalidate = useInvalidateFollowUps();
  return useMutation({
    mutationFn: async (input: CreateFollowUpInput): Promise<DBFollowUp> => {
      const title = input.title.trim();
      if (!title) throw new Error("Tittel mangler");
      if (!input.due_date) throw new Error("Forfall mangler");
      const owner = input.owner_id === undefined ? (activeUser?.id ?? null) : input.owner_id;
      const { data, error } = await supabase
        .from("follow_ups")
        .insert({
          customer_id: input.customer_id,
          opportunity_id: input.opportunity_id ?? null,
          contact_id: input.contact_id ?? null,
          owner_id: owner,
          title,
          description: input.description?.trim() || null,
          due_date: input.due_date,
          priority: input.priority,
          channel: input.channel ?? null,
          source: "manual",
          created_by: user?.id ?? null,
        })
        .select()
        .single();
      if (error) throw error;
      return data as DBFollowUp;
    },
    onSuccess: (row) => invalidate(row),
    onError: onError("Kunne ikke opprette oppfølging"),
  });
}

export interface UpdateFollowUpInput {
  followUp: Pick<DBFollowUp, "id" | "owner_id" | "customer_id">;
  priority?: FollowUpPriority;
  owner_id?: string | null;
  contact_id?: string | null;
  channel?: FollowUpChannel | null;
}

/** Prioritet, eier, kontakt og kanal. Forfall og lukking går via RPC-ene. */
export function useUpdateFollowUp() {
  const qc = useQueryClient();
  const invalidate = useInvalidateFollowUps();
  return useMutation({
    mutationFn: async ({ followUp, ...patch }: UpdateFollowUpInput): Promise<DBFollowUp> => {
      const fields: Record<string, unknown> = {};
      if (patch.priority !== undefined) fields.priority = patch.priority;
      if (patch.owner_id !== undefined) fields.owner_id = patch.owner_id;
      if (patch.contact_id !== undefined) fields.contact_id = patch.contact_id;
      if (patch.channel !== undefined) fields.channel = patch.channel;
      if (Object.keys(fields).length === 0) throw new Error("Ingen endringer å lagre");
      const { data, error } = await supabase
        .from("follow_ups")
        .update(fields)
        .eq("id", followUp.id)
        .select()
        .single();
      if (error) throw error;
      return data as DBFollowUp;
    },
    onSuccess: async (_row, { followUp, owner_id }) => {
      await invalidate(followUp);
      // Byttet eier: både gammel og ny eiers liste og «uten eier»-listen må oppdateres.
      if (owner_id !== undefined) await invalidateFor(qc, "follow_ups", { customerId: followUp.customer_id });
    },
    onError: onError("Kunne ikke lagre oppfølgingen"),
  });
}

export interface SaveFollowUpDraftInput {
  followUp: Pick<DBFollowUp, "id" | "owner_id" | "customer_id">;
  subject: string;
  body: string;
  model: string;
}

/** Cacher generert mailutkast på oppfølgingen, så det ikke genereres på nytt (kost) neste gang kortet åpnes. */
export function useSaveFollowUpDraft() {
  const invalidate = useInvalidateFollowUps();
  return useMutation({
    mutationFn: async ({ followUp, subject, body, model }: SaveFollowUpDraftInput): Promise<void> => {
      const { error } = await supabase
        .from("follow_ups")
        .update({
          draft_subject: subject,
          draft_body: body,
          draft_model: model || null,
          draft_generated_at: new Date().toISOString(),
        })
        .eq("id", followUp.id);
      if (error) throw error;
    },
    onSuccess: (_void, { followUp }) => invalidate(followUp),
    onError: onError("Kunne ikke lagre utkastet"),
  });
}
